import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import {
  Card, Select, SelectItem, Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow, Text, Title,
} from '@tremor/react';
import { fetchAR } from '../api/drilldown';
import { fetchOverview } from '../api/client';
import AccountStatement from '../components/AccountStatement';
import PaymentApplyPanel from '../components/PaymentApplyPanel';
import EmptyState from '../components/EmptyState';
import ErrorRetry from '../components/ErrorRetry';
import TableSkeleton from '../components/TableSkeleton';
import { currentUser } from '../api/auth';
import { t } from '../lib/i18n';
import type { ARCustomer, AgingBucket, User } from '../api/types';
import Header from '../components/Header';
import { canApplyPayments as canApplyPaymentsGate } from '../lib/roles';
import { usePagination } from '../lib/usePagination';
import Pagination from '../components/Pagination';
import DrillCell from '../components/DrillCell';

const fmtMxn = new Intl.NumberFormat('es-MX', {
  style: 'currency',
  currency: 'MXN',
  minimumFractionDigits: 2,
});

function bucket(c: ARCustomer, label: AgingBucket['label']): number {
  return c.aging.find((b) => b.label === label)?.amount_mxn ?? 0;
}

export default function AR() {
  const [user, setUser] = useState<User | null | undefined>(undefined);
  useEffect(() => {
    let alive = true;
    currentUser()
      .then((u) => { if (alive) setUser(u); })
      .catch(() => { if (alive) setUser(null); });
    return () => { alive = false; };
  }, []);
  if (user === undefined) return null;
  return user ? <ARInner user={user} /> : <Navigate to="/login" replace />;
}

function ARInner({ user }: { user: User }) {
  const [params, setParams] = useSearchParams();
  // q lets the Sales page deep-link to one customer; we just filter the
  // already-fetched list client-side rather than re-querying.
  const filterAnum = params.get('q') ?? '';
  const [sort, setSort] = useState<'total' | 'aging90' | 'name'>('total');
  // dir is local since toggling it just re-sorts client-side; the daemon
  // returns full unfiltered list and we apply asc/desc here.
  const [dir, setDir] = useState<'asc' | 'desc'>('desc');
  const [data, setData] = useState<ARCustomer[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Selected account for the detail pane
  const [selectedAnum, setSelectedAnum] = useState<number | null>(null);

  // Resolved sid for AccountStatement / PaymentApplyPanel.
  // Mirror the pattern from Statement.tsx: use sid_assigned if set, else the
  // first store from /api/overview, else 0.
  const [sid, setSid] = useState<number>(user.sid_assigned ?? 0);
  useEffect(() => {
    if (sid > 0) return; // already resolved
    let alive = true;
    fetchOverview(user.role)
      .then((p) => {
        if (!alive) return;
        if (p.stores.length > 0) setSid(p.stores[0].sid);
      })
      .catch(() => { /* silent — AccountStatement will show its own error */ });
    return () => { alive = false; };
  }, [user.role, sid]);

  // Gate: show PaymentApplyPanel only for writes_enabled + owner/accounting
  // (lib/roles canApplyPayments — shared with /pagos and the Clientes menu).
  const canApplyPayments = canApplyPaymentsGate(user);

  // Click a column header → toggle that column's sort direction if it was
  // already selected, otherwise pick desc (the natural read for money cols).
  function clickHeader(col: 'total' | 'aging90' | 'name') {
    if (sort === col) {
      setDir((d) => d === 'desc' ? 'asc' : 'desc');
    } else {
      setSort(col);
      setDir(col === 'name' ? 'asc' : 'desc'); // names asc by default; money desc.
    }
  }
  function sortIcon(col: 'total' | 'aging90' | 'name'): string {
    if (sort !== col) return '⇅';
    return dir === 'desc' ? '↓' : '↑';
  }

  const load = () => {
    let alive = true;
    setLoading(true);
    setError(null);
    fetchAR({ sort, dir: 'desc', limit: 500 })
      .then((rows) => { if (alive) setData(rows); })
      .catch((e: Error) => { if (alive) setError(e.message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  };
  useEffect(() => load(), [sort]);

  const visible = useMemo(() => {
    if (!data) return null;
    let rows = filterAnum === '' ? data : data.filter((c) => String(c.anum) === filterAnum);
    // Local sort applies on top of the daemon sort so the dir toggle is
    // instant — no round-trip for the same data.
    const sorted = rows.slice().sort((a, b) => {
      let cmp = 0;
      if (sort === 'total')   cmp = a.total_mxn - b.total_mxn;
      if (sort === 'aging90') cmp = bucket(a, '90+') - bucket(b, '90+');
      if (sort === 'name')    cmp = a.name.localeCompare(b.name, 'es');
      return dir === 'desc' ? -cmp : cmp;
    });
    return sorted;
  }, [data, filterAnum, sort, dir]);
  const pg = usePagination(visible);

  function setSearch(v: string) {
    const next = new URLSearchParams(params);
    if (v) next.set('q', v); else next.delete('q');
    setParams(next, { replace: true });
  }

  const selectedCustomer = selectedAnum !== null && data
    ? data.find((c) => c.anum === selectedAnum) ?? null
    : null;

  return (
    <div className="min-h-screen flex flex-col">
      <Header user={user} />
      <main className="flex-1 px-6 py-6 space-y-4 max-w-screen-2xl mx-auto w-full">
        <Title>{t.ar.title}</Title>
        <Text className="text-tremor-content text-sm">{t.ar.subtitle}</Text>

        <Card className="flex flex-wrap gap-4 items-end">
          <label className="text-sm flex-1 min-w-64">
            <div className="text-tremor-content text-xs mb-1">{t.ar.cols.account}</div>
            <input
              type="search"
              value={filterAnum}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t.ar.searchPlaceholder}
              className="w-full rounded border border-tremor-border px-3 py-2"
            />
          </label>
          <label className="text-sm">
            <div className="text-tremor-content text-xs mb-1">{t.ar.sort}</div>
            <Select value={sort} onValueChange={(v) => setSort(v as typeof sort)} className="w-48">
              <SelectItem value="total">{t.ar.sortTotal}</SelectItem>
              <SelectItem value="aging90">{t.ar.sortAging90}</SelectItem>
              <SelectItem value="name">{t.ar.sortName}</SelectItem>
            </Select>
          </label>
          <Text className="text-tremor-content text-xs italic self-center">
            o haz clic en un encabezado para ordenar
          </Text>
          {visible && (
            <Text className="ml-auto">{visible.length} {t.common.accounts}</Text>
          )}
        </Card>

        {error && <ErrorRetry message={error} onRetry={load} />}
        {loading && !data && <TableSkeleton rows={8} cols={7} />}
        {visible && visible.length === 0 && !loading && (
          <EmptyState message={t.ar.empty} />
        )}
        {visible && visible.length > 0 && (
          <Card className="p-0 overflow-hidden">
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>{t.ar.cols.account}</TableHeaderCell>
                  <SortableHeader col="name" current={sort} dir={dir} onClick={clickHeader} icon={sortIcon('name')}>
                    {t.ar.cols.name}
                  </SortableHeader>
                  <TableHeaderCell>Antigüedad</TableHeaderCell>
                  <TableHeaderCell className="text-right">{t.ar.cols.b0_30}</TableHeaderCell>
                  <TableHeaderCell className="text-right">{t.ar.cols.b31_60}</TableHeaderCell>
                  <TableHeaderCell className="text-right">{t.ar.cols.b61_90}</TableHeaderCell>
                  <SortableHeader col="aging90" current={sort} dir={dir} onClick={clickHeader} icon={sortIcon('aging90')} align="right">
                    {t.ar.cols.b90p}
                  </SortableHeader>
                  <SortableHeader col="total" current={sort} dir={dir} onClick={clickHeader} icon={sortIcon('total')} align="right">
                    {t.ar.cols.total}
                  </SortableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {pg.pageRows.map((c) => (
                  <TableRow
                    key={c.anum}
                    className={
                      'cursor-pointer ' +
                      (selectedAnum === c.anum ? 'bg-tremor-background-muted dark:bg-dark-tremor-background-muted' : '')
                    }
                    onClick={() => setSelectedAnum((prev) => prev === c.anum ? null : c.anum)}
                  >
                    <TableCell className="font-mono">
                      <span onClick={(e) => e.stopPropagation()}>
                        <DrillCell target={{ kind: 'account', anum: c.anum }}>
                          {c.anum}
                        </DrillCell>
                      </span>
                    </TableCell>
                    <TableCell>
                      {c.name}
                      <Link
                        to={`/estado?anum=${c.anum}`}
                        className="ml-2 text-xs text-tremor-brand hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {t.ar.statementLink}
                      </Link>
                    </TableCell>
                    <TableCell className="w-32">
                      <RowAgingBar customer={c} />
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{fmtMxn.format(bucket(c, '0-30'))}</TableCell>
                    <TableCell className="text-right tabular-nums">{fmtMxn.format(bucket(c, '31-60'))}</TableCell>
                    <TableCell className="text-right tabular-nums">{fmtMxn.format(bucket(c, '61-90'))}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {bucket(c, '90+') > 0 ? (
                        <span className="text-red-600 dark:text-red-400 font-semibold">{fmtMxn.format(bucket(c, '90+'))}</span>
                      ) : (
                        fmtMxn.format(0)
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums font-semibold">{fmtMxn.format(c.total_mxn)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Pagination {...pg} className="px-4" />
          </Card>
        )}

        {/* Detail pane — shown when a row is selected */}
        {selectedCustomer && sid > 0 && (
          <div className="space-y-4 pt-2">
            <div className="flex items-center gap-3">
              <Title className="text-base">{selectedCustomer.name}</Title>
              <Text className="text-xs text-tremor-content">#{selectedCustomer.anum}</Text>
              <button
                type="button"
                className="ml-auto text-xs text-tremor-content hover:text-tremor-content-strong"
                onClick={() => setSelectedAnum(null)}
              >
                ✕ cerrar
              </button>
            </div>
            <AccountStatement sid={sid} anum={selectedCustomer.anum} />
            {canApplyPayments && (
              <PaymentApplyPanel sid={sid} anum={selectedCustomer.anum} user={user} />
            )}
          </div>
        )}
      </main>
    </div>
  );
}

// Sortable column header — renders a TableHeaderCell that responds to click
// and shows the current sort icon. The page tracks (sort, dir) state and
// passes back the toggle handler.
function SortableHeader({
  col, current, dir, onClick, icon, align, children,
}: {
  col: 'total' | 'aging90' | 'name';
  current: string;
  dir: 'asc' | 'desc';
  onClick: (c: 'total' | 'aging90' | 'name') => void;
  icon: string;
  align?: 'right';
  children: React.ReactNode;
}) {
  void current; void dir; // referenced via icon already
  const justify = align === 'right' ? 'justify-end' : 'justify-start';
  return (
    <TableHeaderCell className={align === 'right' ? 'text-right' : ''}>
      <button
        type="button"
        onClick={() => onClick(col)}
        className={`flex items-center gap-1 hover:text-tremor-brand cursor-pointer w-full ${justify}`}
      >
        <span>{children}</span>
        <span className="text-xs text-tremor-content">{icon}</span>
      </button>
    </TableHeaderCell>
  );
}

// Per-row mini stacked aging bar — same color convention as the KPI tile's
// AgingMiniBar (green/amber/orange/red by SAT antigüedad). Width tuned for
// a TableCell; no labels (the bucket columns already show exact amounts).
function RowAgingBar({ customer }: { customer: ARCustomer }) {
  const total = customer.total_mxn;
  if (total === 0) return null;
  const colorByLabel: Record<string, string> = {
    '0-30':  'bg-emerald-500',
    '31-60': 'bg-amber-400',
    '61-90': 'bg-orange-500',
    '90+':   'bg-red-600',
  };
  // Read buckets in canonical order so the bar is always green→red.
  const labels: AgingBucket['label'][] = ['0-30', '31-60', '61-90', '90+'];
  return (
    <div className="h-2 rounded-full bg-tremor-background-muted overflow-hidden flex">
      {labels.map((label) => {
        const amount = bucket(customer, label);
        const pct = (amount / total) * 100;
        if (pct === 0) return null;
        return (
          <div
            key={label}
            className={colorByLabel[label]}
            style={{ width: pct + '%' }}
            title={`${label}: ${fmtMxn.format(amount)}`}
          />
        );
      })}
    </div>
  );
}
